'use client'

import { useMutation } from '@apollo/client'
import type { ApolloError } from '@apollo/client/errors'
import { Label } from '@chain-zen/ui'
import { useState } from 'react'

import { CodeVerificationInput } from '../../components/code-verification'
import { useWorkspaceSetup } from '../../contexts/setup-workspace-context'
import { SEND_VERIFICATION_CODE, VERIFY_CODE_FOR_VERIFIED_STATUS } from '../../data/mutations/workspace'
import { useSetupWorkspaceModal } from '../../stores/setup-workspace-modal-store'
import { VerificationMethodEnum } from '../../types/company'
import { SetupWorkspaceLayout } from './setup-workspace-layout'
/**
 * This component is used to verify the OTP
 */
export const VerificationOTP = () => {
  const { verificationMethod, receiveCodeMethod, setStep } = useWorkspaceSetup()
  const { workspaceID } = useSetupWorkspaceModal()

  const [error, setError] = useState<string | null>(null)

  const context = {
    headers: {
      'ws-id': workspaceID || ''
    }
  }

  // Generates the subtitle for the verification code
  const subtitle =
    receiveCodeMethod === 'PhoneCall'
      ? `We are calling on ${verificationMethod?.value} with the code`
      : `We just sent a verification code to ${verificationMethod?.value}`

  // Verifies the code for workspace verification
  const [verifyCodeForVerifiedStatus, { loading: verifyingCodeStatus }] = useMutation(VERIFY_CODE_FOR_VERIFIED_STATUS, {
    onCompleted: () => {
      setStep(10) // Setting the step to 10 will trigger the next step in the setup process which is the ConfirmationStep component.
    },
    onError: (error) => {
      setError(error.message)
    }
  })

  // Sends a verification code
  const [sendVerificationCode] = useMutation(SEND_VERIFICATION_CODE)

  // Handles the resend of the code
  const handleResendCode = async () => {
    try {
      const variables =
        verificationMethod?.type === VerificationMethodEnum.CompanyEmail
          ? { method: receiveCodeMethod, type: verificationMethod?.type, value: verificationMethod?.value, workspaceID }
          : { method: receiveCodeMethod, type: verificationMethod?.type, workspaceID }

      await sendVerificationCode({
        context,
        variables
      })
    } catch (error) {
      setError((error as ApolloError).message)
    }
  }

  // Handles the submission of the code
  const handleSubmit = async (code: string) => {
    try {
      await verifyCodeForVerifiedStatus({
        context,
        variables: { code }
      })
      return { error: false }
    } catch (error) {
      return { error: true }
    }
  }

  return (
    <SetupWorkspaceLayout title="Enter the code" subtitle={subtitle}>
      <CodeVerificationInput
        emailOrPhone={verificationMethod?.value || ''}
        loading={verifyingCodeStatus}
        onResendCode={handleResendCode}
        onSubmit={handleSubmit}
      />
      {!!error && <Label className="text-red-500">{error}</Label>}
    </SetupWorkspaceLayout>
  )
}
